import { useState, useEffect } from 'react'
import {
  Globe, Copy, Check, Trash2, EyeOff, ExternalLink,
  Loader2, FileText, MessageSquare, Search,
} from 'lucide-react'
import { pagesService, type SinePage } from '@/services/studioService'

// Naviger til samtale via custom event (lyttet på i App.tsx)
function navigateToConversation(conversationId: string) {
  window.dispatchEvent(new CustomEvent('sine:navigate-to-conversation', { detail: { conversationId } }))
}

// Hent favicon for en URL
function getFaviconUrl(pageUrl: string): string {
  try {
    const u = new URL(pageUrl)
    return `https://www.google.com/s2/favicons?domain=${u.hostname}&sz=32`
  } catch {
    return ''
  }
}

export function SinePagesContent() {
  const [pages, setPages] = useState<SinePage[]>([])
  const [loading, setLoading] = useState(true)
  const [copiedId, setCopiedId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [unpublishingId, setUnpublishingId] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  useEffect(() => {
    pagesService.list()
      .then(setPages)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  function handleCopy(page: SinePage) {
    const url = page.public_url || `${window.location.origin}/p/${page.slug}`
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(page.id)
      setTimeout(() => setCopiedId(null), 2000)
    })
  }

  async function handleDelete(id: string) {
    setDeletingId(id)
    try {
      await pagesService.delete(id)
      setPages(prev => prev.filter(p => p.id !== id))
    } catch {
      // ignore
    } finally {
      setDeletingId(null)
    }
  }

  async function handleUnpublish(id: string) {
    setUnpublishingId(id)
    try {
      await pagesService.unpublish(id)
      setPages(prev => prev.map(p => p.id === id ? { ...p, published: false } : p))
    } catch {
      // ignore
    } finally {
      setUnpublishingId(null)
    }
  }

  const filtered = search.trim()
    ? pages.filter(p =>
        p.title.toLowerCase().includes(search.toLowerCase()) ||
        (p.description || '').toLowerCase().includes(search.toLowerCase())
      )
    : pages

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 className="settings-title" style={{ margin: 0 }}>Sine Pages</h2>
        {/* Search */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: '#1A1A1A', border: '1px solid #2A2A2A',
          borderRadius: 8, padding: '6px 12px', width: 200,
        }}>
          <Search size={13} style={{ color: '#5A5A5A', flexShrink: 0 }} />
          <input
            type="text"
            placeholder="Søk etter side..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              background: 'none', border: 'none', outline: 'none',
              color: '#E5E5E5', fontSize: 12, fontFamily: 'inherit', width: '100%',
            }}
          />
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '32px 0', justifyContent: 'center' }}>
          <Loader2 size={16} style={{ color: '#5A5A5A', animation: 'spin 1s linear infinite' }} />
          <span style={{ fontSize: 13, color: '#5A5A5A' }}>Laster sider...</span>
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', padding: '60px 0', gap: 12,
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: 12, background: '#1A1A1A',
            border: '1px solid #2A2A2A', display: 'flex', alignItems: 'center',
            justifyContent: 'center',
          }}>
            <FileText size={20} style={{ color: '#3A3A3A' }} />
          </div>
          <div style={{ fontSize: 14, color: '#5A5A5A', textAlign: 'center' }}>
            {search ? 'Ingen sider matcher søket' : 'Ingen publiserte sider ennå'}
          </div>
          {!search && (
            <div style={{ fontSize: 12, color: '#3A3A3A', textAlign: 'center', maxWidth: 280 }}>
              Be Sine om å publisere en rapport eller analyse som en Sine Page.
            </div>
          )}
        </div>
      )}

      {/* Pages list — Tilkoblinger-stil */}
      {!loading && filtered.length > 0 && (
        <div className="conn-app-list">
          {filtered.map(page => {
            const url = page.public_url || `${window.location.origin}/p/${page.slug}`
            const faviconUrl = getFaviconUrl(url)
            return (
              <div key={page.id} className="conn-app-row" style={{ alignItems: 'flex-start', paddingTop: 14, paddingBottom: 14 }}>
                {/* Favicon / icon */}
                <div className="conn-icon-wrap" style={{ background: '#1A1A1A', flexShrink: 0, marginTop: 2 }}>
                  {faviconUrl ? (
                    <img
                      src={faviconUrl}
                      alt=""
                      style={{ width: 20, height: 20, borderRadius: 4 }}
                      onError={e => { (e.target as HTMLImageElement).style.display = 'none' }}
                    />
                  ) : (
                    <Globe size={16} style={{ color: page.published ? '#3B82F6' : '#5A5A5A' }} />
                  )}
                </div>

                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* Name + status badge */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span className="conn-app-name" style={{ fontSize: 14 }}>{page.title}</span>
                    {!page.published && (
                      <span style={{
                        fontSize: 10, fontWeight: 600, color: '#888',
                        background: '#222', border: '1px solid #2A2A2A',
                        borderRadius: 4, padding: '1px 6px',
                      }}>
                        Avpublisert
                      </span>
                    )}
                  </div>

                  {/* Description */}
                  {page.description && (
                    <div className="conn-app-desc" style={{ marginBottom: 6 }}>
                      {page.description}
                    </div>
                  )}

                  {/* URL row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 11, color: '#4A7FBF', fontFamily: 'monospace',
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: 260, textDecoration: 'none', flexShrink: 1,
                      }}
                      title={url}
                    >
                      {url.replace(/^https?:\/\//, '')}
                    </a>
                    {/* Copy URL button */}
                    <button
                      onClick={() => handleCopy(page)}
                      title="Kopier URL"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 22, height: 22, borderRadius: 5,
                        border: '1px solid #2A2A2A', background: 'none',
                        color: copiedId === page.id ? '#5EB92D' : '#5A5A5A',
                        cursor: 'pointer', flexShrink: 0, transition: 'color 0.15s',
                      }}
                    >
                      {copiedId === page.id ? <Check size={11} /> : <Copy size={11} />}
                    </button>
                  </div>
                </div>

                {/* Right actions */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginTop: 2 }}>
                  {/* Go to conversation */}
                  {page.conversation_id && (
                    <button
                      onClick={() => navigateToConversation(page.conversation_id!)}
                      title="Gå til samtalen"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: '5px 10px', borderRadius: 7,
                        border: '1px solid #2A2A2A', background: 'none',
                        color: '#7A7A7A', fontSize: 12, cursor: 'pointer',
                        fontFamily: 'inherit', whiteSpace: 'nowrap',
                        transition: 'color 0.15s, border-color 0.15s',
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLButtonElement).style.color = '#E5E5E5'
                        ;(e.currentTarget as HTMLButtonElement).style.borderColor = '#3A3A3A'
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLButtonElement).style.color = '#7A7A7A'
                        ;(e.currentTarget as HTMLButtonElement).style.borderColor = '#2A2A2A'
                      }}
                    >
                      <MessageSquare size={12} />
                      Samtale
                    </button>
                  )}

                  {/* Open page */}
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Åpne siden"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      padding: '5px 10px', borderRadius: 7,
                      border: '1px solid #2A2A2A', background: 'none',
                      color: '#7A7A7A', fontSize: 12, cursor: 'pointer',
                      fontFamily: 'inherit', textDecoration: 'none', whiteSpace: 'nowrap',
                      transition: 'color 0.15s, border-color 0.15s',
                    }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLAnchorElement).style.color = '#E5E5E5'
                      ;(e.currentTarget as HTMLAnchorElement).style.borderColor = '#3A3A3A'
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLAnchorElement).style.color = '#7A7A7A'
                      ;(e.currentTarget as HTMLAnchorElement).style.borderColor = '#2A2A2A'
                    }}
                  >
                    <ExternalLink size={12} />
                    Åpne
                  </a>

                  {/* Unpublish */}
                  {page.published && (
                    <button
                      onClick={() => handleUnpublish(page.id)}
                      disabled={unpublishingId === page.id}
                      title="Avpubliser"
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 28, height: 28, borderRadius: 7,
                        border: '1px solid #2A2A2A', background: 'none',
                        color: '#5A5A5A', cursor: 'pointer',
                      }}
                    >
                      {unpublishingId === page.id
                        ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        : <EyeOff size={12} />}
                    </button>
                  )}

                  {/* Delete */}
                  <button
                    onClick={() => handleDelete(page.id)}
                    disabled={deletingId === page.id}
                    title="Slett siden"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 28, height: 28, borderRadius: 7,
                      border: '1px solid #2A2A2A', background: 'none',
                      color: '#5A5A5A', cursor: 'pointer',
                    }}
                  >
                    {deletingId === page.id
                      ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} />
                      : <Trash2 size={12} />}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
